/**
 * Labstep
 *
 * @module models/share-link
 * @desc Typescript export class for ShareLink
 */

import { Type } from 'class-transformer';
import { Device } from 'labstep-web/models/device.model';
import { Entity } from 'labstep-web/models/entity.model';
import { EntityStateWorkflow } from 'labstep-web/models/entity-state-workflow.model';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { Folder } from 'labstep-web/models/folder.model';
import { Group } from 'labstep-web/models/group.model';
import { OrderRequest } from 'labstep-web/models/order-request.model';
import { Organization } from 'labstep-web/models/organization.model';
import type { PermissionType } from 'labstep-web/models/permission.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { Resource } from 'labstep-web/models/resource.model';
import { User } from 'labstep-web/models/user.model';

export enum ShareLinkType {
  view = 'view',
  edit = 'edit',
}

export class ShareLink extends Entity {
  static readonly entityName = 'share_link' as const;

  get entityName(): string {
    return ShareLink.entityName;
  }

  constructor(data: Partial<ShareLink> = {}) {
    super();
    Object.assign(this, data);
  }

  id!: number;

  token!: string;

  type!: ShareLinkType | PermissionType;

  // For typescript to allow this field in POST ShareLink
  is_regenerate!: boolean;

  @Type(() => User)
  author!: User;

  @Type(() => EntityStateWorkflow)
  entity_state_workflow!: EntityStateWorkflow;

  @Type(() => ExperimentWorkflow)
  experiment_workflow!: ExperimentWorkflow;

  @Type(() => Group)
  group!: Group;

  @Type(() => ProtocolCollection)
  protocol_collection!: ProtocolCollection;

  @Type(() => Resource)
  resource!: Resource;

  @Type(() => Organization)
  organization!: Organization;

  @Type(() => Device)
  device!: Device;

  @Type(() => OrderRequest)
  order_request!: OrderRequest;

  @Type(() => Folder)
  folder!: Folder;

  get entity():
    | EntityStateWorkflow
    | ExperimentWorkflow
    | Device
    | ProtocolCollection
    | Resource
    | Organization
    | Group
    | OrderRequest
    | Folder {
    return (
      this.entity_state_workflow ||
      this.experiment_workflow ||
      this.device ||
      this.protocol_collection ||
      this.resource ||
      this.group ||
      this.organization ||
      this.order_request ||
      this.folder
    );
  }
}
